import { useEffect } from "react";
import { format as formatDate } from "small-date";
import type { CountdownState } from "../hooks/useCountdown";
import { useCountdown } from "../hooks/useCountdown";
import { cn } from "../lib/utils";

export interface CountdownBaseProps {
  length: number;
  isRunning: boolean;
  onDone?: () => void;
  onStateChange?: (state: CountdownState) => void;
}

// NUMERIC COUNTDOWN
// -----------------

export interface NumberCountdownProps
  extends CountdownBaseProps,
    React.HTMLAttributes<HTMLDivElement> {
  format?: string;
}

export function NumberCountdown({
  length,
  isRunning,
  format = "mm:ss",
  onDone,
  onStateChange,
  className,
  ...divProps
}: NumberCountdownProps) {
  const [remaining, { start, stop, state }] = useCountdown(length, { onDone });

  if (isRunning === true && state === "idle") {
    start();
  }

  if (isRunning === false && state === "counting") {
    stop();
  }

  useEffect(() => {
    onStateChange?.(state);
  }, [onStateChange, state]);

  return (
    <div {...divProps} className={cn("text-8xl font-bold tabular-nums", className)}>
      {formatDate(new Date(remaining), format)}
    </div>
  );
}

// PROGRESS COUNTDOWN
// ------------------

export interface ProgressCountdownProps
  extends CountdownBaseProps,
    React.HTMLAttributes<HTMLDivElement> {
  backgroundClassName?: string;
  foregroundClassName?: string;
  textClassName?: string;
}

export function ProgressCountdown({
  length,
  isRunning,
  onDone,
  onStateChange,
  backgroundClassName,
  foregroundClassName,
  textClassName,
  className,
  ...divProps
}: ProgressCountdownProps) {
  const [remaining, { start, stop, state }] = useCountdown(length, { onDone });
  const percentage = Math.round(100 - (remaining / length) * 100);

  if (isRunning === true && state === "idle") {
    start();
  }

  if (isRunning === false && state === "counting") {
    stop();
  }

  useEffect(() => {
    onStateChange?.(state);
  }, [onStateChange, state]);

  return (
    <div {...divProps} className={className}>
      <div className="flex items-center gap-2">
        <div className={cn("h-4 w-full overflow-clip rounded-full bg-muted", backgroundClassName)}>
          <div
            className={cn(
              "h-full rounded-full bg-primary transition-all ease-linear",
              foregroundClassName,
            )}
            style={{
              width: `${percentage}%`,
              transitionDuration: `${length / 100}ms`,
            }}
          />
        </div>
        <div
          className={cn("flex justify-between tabular-nums text-muted-foreground", textClassName)}
        >
          {formatDate(new Date(remaining), "mm:ss")}
        </div>
      </div>
    </div>
  );
}

// CIRCULAR COUNTDOWN
// ------------------

export interface CircularCountdownProps
  extends CountdownBaseProps,
    React.SVGAttributes<SVGSVGElement> {
  colorBackground?: string;
  colorForeground?: string;
}

export function CircularCountdown({
  length,
  isRunning,
  onDone,
  onStateChange,
  colorBackground,
  colorForeground,
  ...svgProps
}: CircularCountdownProps) {
  const [remaining, { start, stop, state }] = useCountdown(length, { onDone });
  const percentage = remaining / length;
  const x = 50 + 50 * Math.cos(percentage * 2 * Math.PI);
  const y = 50 + 50 * Math.sin(percentage * 2 * Math.PI);

  const backgroundColor = colorBackground || "hsl(var(--muted))";
  const foregroundColor = colorForeground || "hsl(var(--primary))";

  if (isRunning === true && state === "idle") {
    start();
  }

  if (isRunning === false && state === "counting") {
    stop();
  }

  useEffect(() => {
    onStateChange?.(state);
  }, [onStateChange, state]);

  return (
    <svg {...svgProps} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <circle cx={50} cy={50} r={50} fill={state === "idle" ? foregroundColor : backgroundColor} />
      <path
        d={`M50 50 L100 50 A50 50 0 ${percentage > 0.5 ? 1 : 0} 1 ${x} ${y}Z`}
        fill={foregroundColor}
        transform="rotate(-90 50 50) scale(1, -1) translate(0, -100)"
      />
    </svg>
  );
}
